import React, { useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  FormControl,
  Divider,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const FORMAS_PAGO = [
  { codigo: "20", nombre: "Otros con Utilización del Sistema Financiero" },
  { codigo: "01", nombre: "Sin utilización del Sistema Financiero" },
  { codigo: "15", nombre: "Compensación de Deudas" },
  { codigo: "16", nombre: "Tarjeta de débito" },
  { codigo: "17", nombre: "Dinero Electrónico" },
  { codigo: "18", nombre: "Tarjeta Prepago" },
  { codigo: "19", nombre: "Tarjeta de crédito" },
  { codigo: "21", nombre: "Endoso de títulos" },
];

const defaultRow = (valor = "", forma = "20") => ({
  forma_pago: forma,
  plazo: "0",
  unidad_tiempo: "dias",
  valor,
});

const toMoney2 = (n) => {
  const x = Number(n || 0);
  return Number.isFinite(x) ? x.toFixed(2) : "0.00";
};

const cleanMoneyInput = (value) => {
  const v = String(value ?? "").replace(",", ".");
  if (v === "") return "";
  const match = v.match(/^\d*(?:\.\d{0,2})?$/);
  if (match) return v;
  const only = v.replace(/[^\d.]/g, "");
  const parts = only.split(".");
  if (parts.length === 1) return parts[0];
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export default function InvoicePayments({ totalPagar, payments, setPayments }) {
  const safePayments = Array.isArray(payments) ? payments : [];

  useEffect(() => {
    if (!safePayments.length) {
      setPayments([defaultRow(toMoney2(totalPagar), "20")]);
      return;
    }

    if (safePayments.length === 1 && String(safePayments[0].forma_pago) === "20") {
      const nextValor = toMoney2(totalPagar);
      if (String(safePayments[0].valor ?? "") !== nextValor) {
        setPayments([{ ...safePayments[0], valor: nextValor }]);
      }
      return;
    }

    const idxAuto = safePayments.findIndex((r) => String(r.forma_pago) === "20");
    if (idxAuto >= 0) {
      const sumOther = safePayments.reduce((acc, r, i) => {
        if (i === idxAuto) return acc;
        return acc + toNum(r.valor);
      }, 0);
      const rem = Math.max(0, toNum(totalPagar) - sumOther);
      const nextValor = toMoney2(rem);
      if (String(safePayments[idxAuto].valor ?? "") !== nextValor) {
        setPayments(
          safePayments.map((r, i) => (i === idxAuto ? { ...r, valor: nextValor } : r))
        );
      }
    }
  }, [totalPagar, safePayments, setPayments]);

  const rows = safePayments.length ? safePayments : [defaultRow(toMoney2(totalPagar), "20")];

  const totalAsignado = useMemo(
    () => rows.reduce((acc, r) => acc + toNum(r.valor), 0),
    [rows]
  );

  const isMatch = toMoney2(totalPagar) === toMoney2(totalAsignado);

  const addRow = () => {
    const has20 = rows.some((r) => String(r.forma_pago) === "20");
    const next = [...rows, defaultRow("", has20 ? "01" : "20")];
    setPayments(next);
  };

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    setPayments(next.length ? next : [defaultRow(toMoney2(totalPagar), "20")]);
  };

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));

    if (patch.forma_pago !== undefined) {
      const chosen = String(patch.forma_pago);
      if (chosen === "20") {
        const first20 = next.findIndex((r) => String(r.forma_pago) === "20");
        const other20 = next.findIndex((r, i) => String(r.forma_pago) === "20" && i !== first20);
        if (other20 >= 0) next[other20] = { ...next[other20], forma_pago: "01" };

        const idxAuto = next.findIndex((r) => String(r.forma_pago) === "20");
        if (idxAuto >= 0) {
          const sumOther = next.reduce((acc, r, i) => {
            if (i === idxAuto) return acc;
            return acc + toNum(r.valor);
          }, 0);
          const rem = Math.max(0, toNum(totalPagar) - sumOther);
          next[idxAuto] = { ...next[idxAuto], valor: toMoney2(rem) };
        }
      }
    }

    setPayments(next);
  };

  const idxAuto = rows.findIndex((r) => String(r.forma_pago) === "20");

  return (
    <Box>
      <Paper sx={{ borderRadius: 2, border: "1px solid #e6e6e6" }}>
        <Box sx={{ bgcolor: "#f3f3f3", px: 2, py: 1.25 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={5}>
              <Typography fontWeight={700}>Forma de pago</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography fontWeight={700}>Plazo</Typography>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Typography fontWeight={700}>Unid. de tiempo</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography fontWeight={700}>Valor</Typography>
            </Grid>
            <Grid item xs={12} md={0.5} />
          </Grid>
        </Box>

        <Box sx={{ p: 2 }}>
          {rows.map((r, idx) => {
            const isAuto = idx === idxAuto && String(r.forma_pago) === "20";
            return (
              <Grid
                container
                spacing={1.5}
                key={idx}
                alignItems="center"
                sx={{ mb: idx === rows.length - 1 ? 0 : 2 }}
              >
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth>
                    <Select
                      value={r.forma_pago}
                      onChange={(e) => updateRow(idx, { forma_pago: e.target.value })}
                    >
                      {FORMAS_PAGO.map((f) => (
                        <MenuItem key={f.codigo} value={f.codigo}>
                          {f.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    value={r.plazo}
                    onChange={(e) =>
                      updateRow(idx, {
                        plazo: e.target.value.replace(/\D/g, "").slice(0, 4) || "0",
                      })
                    }
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <FormControl fullWidth>
                    <Select
                      value={r.unidad_tiempo}
                      onChange={(e) => updateRow(idx, { unidad_tiempo: e.target.value })}
                    >
                      <MenuItem value="dias">días</MenuItem>
                      <MenuItem value="meses">meses</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    value={r.valor}
                    onChange={(e) => updateRow(idx, { valor: cleanMoneyInput(e.target.value) })}
                    inputProps={{ inputMode: "decimal" }}
                    fullWidth
                    disabled={isAuto}
                  />
                </Grid>

                <Grid item xs={12} md={0.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton onClick={() => removeRow(idx)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}

          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addRow}>
              Agregar detalle
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 3 }}>
          <Box textAlign="right">
            <Typography fontWeight={700}>Total a pagar</Typography>
            <Typography>${toMoney2(totalPagar)}</Typography>
          </Box>

          <Box textAlign="right">
            <Typography fontWeight={700}>Total asignado</Typography>
            <Typography sx={{ color: isMatch ? "#2e7d32" : "#b71c1c" }}>
              ${toMoney2(totalAsignado)}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
